import { InjectionToken } from '@angular/core';

/**
 * Tenant timezone + date-format for rendering order/coupon dates etc. Each app
 * adapts its own tenant-settings source (mirrors DASHBOARD_TIMEZONE).
 */
export interface StorefrontTimeZoneConfig {
  getTimeZone(): string;
  getDateFormat?(): string;
}

export const STOREFRONT_TIMEZONE = new InjectionToken<StorefrontTimeZoneConfig>(
  'STOREFRONT_TIMEZONE',
  { providedIn: 'root', factory: () => ({ getTimeZone: () => 'UTC' }) },
);

export const DEFAULT_STOREFRONT_DATE_FORMAT = 'MM/DD/YYYY' as const;
export type StorefrontDateFormat =
  | typeof DEFAULT_STOREFRONT_DATE_FORMAT
  | 'DD/MM/YYYY'
  | 'YYYY-MM-DD';

export function normalizeStorefrontTimeZone(timeZone?: string | null): string {
  const candidate = timeZone?.trim() || 'UTC';
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: candidate }).format();
    return candidate;
  } catch {
    return 'UTC';
  }
}

export function normalizeStorefrontDateFormat(dateFormat?: string | null): StorefrontDateFormat {
  switch (dateFormat) {
    case 'DD/MM/YYYY':
    case 'YYYY-MM-DD':
    case 'MM/DD/YYYY':
      return dateFormat;
    default:
      return DEFAULT_STOREFRONT_DATE_FORMAT;
  }
}

/** Tenant-aware date formatting for order/coupon dates etc. (mirrors formatDashboardDate). */
export function formatStorefrontDate(
  value?: string | null,
  timeZone?: string | null,
  dateFormat?: string | null,
  variant: 'numeric' | 'short' | 'long' = 'short',
): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const zone = normalizeStorefrontTimeZone(timeZone);
  const format = normalizeStorefrontDateFormat(dateFormat);
  const monthStyle: '2-digit' | 'short' | 'long' = variant === 'numeric'
    ? '2-digit'
    : variant === 'long'
      ? 'long'
      : 'short';

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    year: 'numeric',
    month: monthStyle,
    day: '2-digit',
  });

  const values = new Map(
    formatter
      .formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  );

  const parts = {
    month: values.get('month') ?? '',
    day: values.get('day') ?? '',
    year: values.get('year') ?? '',
  };

  if (variant === 'numeric') {
    switch (format) {
      case 'DD/MM/YYYY':
        return `${parts.day}/${parts.month}/${parts.year}`;
      case 'YYYY-MM-DD':
        return `${parts.year}-${parts.month}-${parts.day}`;
      default:
        return `${parts.month}/${parts.day}/${parts.year}`;
    }
  }

  switch (format) {
    case 'DD/MM/YYYY':
      return `${parts.day} ${parts.month} ${parts.year}`;
    case 'YYYY-MM-DD':
      return `${parts.year} ${parts.month} ${parts.day}`;
    default:
      return `${parts.month} ${parts.day}, ${parts.year}`;
  }
}
